import { useState, memo, useEffect } from 'react';
import { type IconName } from '@fortawesome/fontawesome-svg-core';
import {
  AlertMessage,
  FAIcon,
  TextLink,
  TextLinkUnderlineStyle,
  Theme,
  useTranslation,
  Modal,
  Button,
  ButtonStyles,
  ButtonWidth,
  useSession,
  Breakpoints,
  useBreakpoint,
} from '@nextgen-web-framework/all';
import { useAddressUpdate } from '@commerce-webcomponents/ui';
import { type AppState, StorageService } from '@nextgen-web-framework/core';
import { useSelector, useDispatch } from 'react-redux';
import {
  AccountProcessType,
  RenewalFlag,
  Cart,
  CartConstantsEnum,
  CartEventsEnum,
  InitialCartState,
  OrderType,
  PublishEvent,
  cartApi,
  getInstallProductDetails,
  getOrderTypeConfig,
  isInstallmentUnavailable,
  isProxyOrder,
  switchToRegularCart,
  updateCart,
  updateShippingAddressInLS,
  createOrFetchCart,
  isGroupOrder,
} from '@commerce-cart-webcomponents/sdk';
import styles from './ProxyOrderBanner.module.scss';
import { CartTypeSelectionDrawer } from './cart-type-selection-drawer/CartTypeSelectionDrawer';
import { ProxyOrderBannerProps } from './types/propsType';

export const ProxyOrderBanner = memo(({ onSwitchSuccess }: ProxyOrderBannerProps) => {
  const t = useTranslation();
  const { updateAddress } = useAddressUpdate();
  const { isABO } = useSession();
  const dispatch = useDispatch();
  const viewPort = useBreakpoint();
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [cartOrderType, setCartOrderType] = useState<string>('');
  const [showAccountSearch, setShowAccountSearch] = useState(false);
  const [modalHeadLine, setModalHeadLine] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [showRenewalKitModal, setShowRenewalKitModal] = useState(false);
  const dataEntries = cart?.subCarts?.[0]?.entries ?? [];
  const installmentProducts = dataEntries && getInstallProductDetails(dataEntries);
  const showInstallmentAlertModal = installmentProducts?.length || isInstallmentUnavailable(dataEntries);

  const regularCartText = t('cart.proxyOrderBanner.regularCart');
  const downLineCartText = t('cart.proxyOrderBanner.downLineCart');
  const gcoCartText = t('cart.proxyOrderBanner.groupCart');
  const changeText = t('cart.proxyOrderBanner.change');
  const cancelText = t('cart.cartTypeSelectionDrawer.cancel');
  const continueText = t('cart.cartTypeSelectionDrawer.continue');
  const downLineCartDesc = t('cart.proxyOrderBanner.downLineCartDescription');
  const isMobileView = [Breakpoints.XS, Breakpoints.SM].includes(viewPort as Breakpoints);

  const multipleCartModalHeadLine = t('cart.proxyOrderBanner.orderForMultipleCart');

  const enableMultipleCartText = t('cart.proxyOrderBanner.enableMultipleCart');
  const disableMultipleCartText = t('cart.proxyOrderBanner.disableMultipleCart');
  const { accountProcess, amwayUser } = cart;

  useEffect(() => {
    if (!cartOrderType && cart.orderType) {
      setCartOrderType(cart.orderType);
    }
  }, [cart.orderType]);

  const membershipExpired = amwayUser?.renewalFlag === RenewalFlag.MustRenew;
  const renewalKitModal =
    cart.orderType === OrderType.Regular &&
    accountProcess &&
    accountProcess?.type === AccountProcessType.Renewal &&
    (amwayUser?.renewalFlag === RenewalFlag.CanRenew || membershipExpired);

  const handleTextLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    renewalKitModal ? setShowRenewalKitModal(true) : setShowDrawer(true);
  };

  const handleOnClose = () => {
    setShowDrawer(false);
    setCartOrderType(cart.orderType);
  };

  const handleModalOnClose = () => {
    setShowConfirmModal(false);
    setShowDrawer(true);
    setCartOrderType(cart.orderType);
  };

  const getText = (orderType: string) => {
    switch (orderType) {
      case OrderType.Proxy:
        return downLineCartText;
      case OrderType.Gco:
        return gcoCartText;
      default:
        return regularCartText;
    }
  };

  const getIcon = (orderType: string): IconName => {
    switch (orderType) {
      case OrderType.Proxy:
        return 'user-plus';
      case OrderType.Gco:
        return 'user-group';
      default:
        return 'user';
    }
  };

  const radioChange = (closeDrawer = true) => {
    if (isMobileView) {
      setShowDrawer(!closeDrawer);
    }
    setShowConfirmModal(closeDrawer);
  };

  const handleOnRadioChange = (orderType: OrderType) => {
    if (cartOrderType === OrderType.Gco) {
      setModalHeadLine(orderType === OrderType.Proxy ? downLineCartText : regularCartText);
      setModalContent(disableMultipleCartText);
      radioChange(orderType !== OrderType.Proxy);
    }
    if (showInstallmentAlertModal && isProxyOrder(orderType)) {
      setModalHeadLine(downLineCartText);
      setModalContent(downLineCartDesc);
      radioChange();
      return;
    }

    setCartOrderType(orderType);
    switch (orderType) {
      case OrderType.Gco:
        setModalHeadLine(multipleCartModalHeadLine);
        setModalContent(enableMultipleCartText);
        radioChange(!isGroupOrder(cart.orderType));
        return;
      case OrderType.Proxy:
        setShowConfirmModal(false);
        return;
      default:
        return;
    }
  };

  const continueModalClick = async () => {
    if (modalHeadLine === downLineCartText) {
      setShowConfirmModal(false);
      isMobileView && setShowDrawer(true);
      setShowAccountSearch(true);
      return;
    }
    if (cartOrderType === OrderType.Proxy) {
      setShowAccountSearch(true);
      return;
    }

    if (cartOrderType === OrderType.Regular) {
      const res = (await switchToRegularCart(cart)) as Cart;
      PublishEvent({
        cartEvent: CartEventsEnum.UpdateMiniCartIcon,
        cart: res,
      });
      dispatch(updateCart(res));
    }

    if (cartOrderType === OrderType.Gco) {
      let mainCartId = cart.id;
      const cartIdFromLS = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
      if (!cartIdFromLS) {
        mainCartId = (await createOrFetchCart()).id;
      }
      const res = (await cartApi.updateGCOFlag({
        cartId: mainCartId,
        payload: {
          orderType: OrderType.Gco,
          version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
        },
        options: {
          skipCartIdStoreInLS: true,
          skipCartResStoreInLS: true,
          skipVersionStoreInLS: false,
        },
      })) as Cart;
      updateShippingAddressInLS(res.amwayUser.amwayUserId, res.amwayUser.accountId as string).then(updateAddress);
      onSwitchSuccess?.();
      dispatch(updateCart(res));
      PublishEvent({
        cartEvent: CartEventsEnum.UpdateMiniCartIcon,
        cart: res,
      });
    }
    setShowDrawer(false);
    setShowConfirmModal(false);
  };

  const getOrderTypeFromConfig = () => (isABO() && cart.id === '' ? getOrderTypeConfig() : cart.orderType);

  return (
    <>
      {showRenewalKitModal && (
        <Modal
          className={styles['alert-message-modal']}
          headline={
            membershipExpired ? t('cart.proxyOrderBanner.renewalRequired') : t('cart.proxyOrderBanner.renewalKitInCart')
          }
          primaryButtonLabel=""
          closeIcon={false}
          useReactPortal={true}
          portalContainerId="react-portal-modal"
        >
          <div className={styles['alert-message-modal-container']}>
            {membershipExpired
              ? t('cart.proxyOrderBanner.accountExpiredDetails')
              : t('cart.proxyOrderBanner.completeABORenewalFirst')}
            <div className={styles['alert-message-modal-container-buttons']}>
              <Button
                onClick={() => setShowRenewalKitModal(false)}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                title={
                  membershipExpired ? t('cart.proxyOrderBanner.continueRenewal') : t('cart.itemsUnavailable.reviewCart')
                }
              />
            </div>
          </div>
        </Modal>
      )}
      {showConfirmModal && (
        <Modal
          className={styles['alert-message-modal']}
          headline={modalHeadLine}
          primaryButtonLabel=""
          onClose={() => setShowConfirmModal(false)}
          useReactPortal={true}
          portalContainerId="react-portal-modal"
        >
          <div className={styles['alert-message-modal-container']}>
            {modalContent}
            <div className={styles['alert-message-modal-container-buttons']}>
              <Button onClick={continueModalClick} buttonWidth={ButtonWidth.FULL_WIDTH} title={continueText} />
              <Button
                onClick={handleModalOnClose}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                buttonStyle={ButtonStyles.OUTLINE}
                title={cancelText}
              />
            </div>
          </div>
        </Modal>
      )}
      <div className={styles['alert-message-wrapper-container']}>
        <AlertMessage className={styles['alert-message-wrapper']} hasIcon={false} theme={Theme.GRAY}>
          <div className={styles['alert-message-banner']}>
            <div className={styles['alert-message-banner-icon-and-text']}>
              <FAIcon className={styles['alert-message-banner-icon']} iconName={getIcon(getOrderTypeFromConfig())} />
              <span className={styles['alert-message-banner-text']}>{getText(getOrderTypeFromConfig())}</span>
            </div>

            <span className={styles['alert-message-banner-change']}>
              <TextLink onClick={handleTextLinkClick} underline={TextLinkUnderlineStyle.FIXED} text={changeText} />
              <FAIcon iconName="chevron-down" theme={Theme.BLACK} onClick={handleTextLinkClick} />
            </span>
          </div>
        </AlertMessage>
      </div>
      {showDrawer && (
        <CartTypeSelectionDrawer
          displayAccountSearch={showAccountSearch}
          toggleDisplayAccountSearch={setShowAccountSearch}
          cartData={cart}
          onRadioChange={handleOnRadioChange}
          initalOrderType={cartOrderType}
          onClose={handleOnClose}
          toggleDrawer={() => setShowDrawer(false)}
        />
      )}
    </>
  );
});

ProxyOrderBanner.displayName = 'ProxyOrderBanner';
